import { HttpClient } from "@angular/common/http";
import { inject, Injectable, signal, WritableSignal } from "@angular/core";
import { Onboarding } from "../models/onboarding.model";
import { APIResponse } from "app/shared/types/common.type";
import { apiUrl } from "environments/environment.helper";
import { catchError, finalize, map, Observable, of, shareReplay, throwError } from "rxjs";

export type OnboardingListResponse = APIResponse<Onboarding[]>;
export type OnboardingResponse = APIResponse<Onboarding>;

@Injectable({
  providedIn: "root",
})
export class OnboardingService {
  private readonly _http = inject(HttpClient);
  private _hasLoadedUserOnboardings = false;
  private _pendingOnboardingsRequest$: Observable<Onboarding[]> | null = null;

  public onboardings: WritableSignal<Onboarding[]> = signal([]);
  // Onboardings related to the currently logged-in user (role-matched), used by onboarding-view.
  public userOnboardings: WritableSignal<Onboarding[]> = signal([]);
  public pendingOnboardings: WritableSignal<Onboarding[]> = signal([]);
  public activeOnboarding: WritableSignal<Onboarding | null> = signal(null);

  /**
   * Helper to check if a current URL matches an onboarding route configuration.
   * Supports exact matches, optional wildcard prefix matches (route ending with `/*`),
   * and dynamic path parameter placeholders (e.g. `:id` or `{ids}`).
   */
  matchRoute(url: string, route: string, exact: boolean = false): boolean {
    const normalize = (path: string): string => {
      if (!path) return "/home";
      let clean = path.split("?")[0].split("#")[0].trim();
      if (clean !== "/" && !clean.startsWith("/")) {
        clean = "/" + clean;
      }
      if (clean.length > 1 && clean.endsWith("/")) {
        clean = clean.slice(0, -1);
      }
      return clean === "/" || clean === "" || clean === "/home" ? "/home" : clean;
    };

    const nUrl = normalize(url);
    const routeTrimmed = (route ?? "").trim();
    const hasWildcard = routeTrimmed.endsWith("/*");
    const routeForMatch = hasWildcard ? routeTrimmed.slice(0, -2) : routeTrimmed;
    const nRoute = normalize(routeForMatch);

    // 1. Direct string match
    if (nUrl === nRoute) return true;

    // 2. Wildcard prefix match (`/path/*`) - only when not exact
    if (!exact && hasWildcard && nUrl.startsWith(nRoute + "/")) return true;

    // 3. Dynamic parameter matching ({ids} or :id)
    if (nRoute.includes("{") || nRoute.includes(":")) {
      const uSegs = nUrl.split("/").filter(Boolean);
      const rSegs = nRoute.split("/").filter(Boolean);

      if (exact && uSegs.length !== rSegs.length) return false;
      if (!exact && uSegs.length < rSegs.length) return false;

      for (let i = 0; i < rSegs.length; i++) {
        const rs = rSegs[i];
        const us = uSegs[i];
        const isParam = rs.startsWith(":") || (rs.startsWith("{") && rs.endsWith("}"));
        if (!isParam && rs.toLowerCase() !== us.toLowerCase()) return false;
      }
      return true;
    }

    return false;
  }

  show(id: number): void {
    const match = this.userOnboardings().find(o => o.id === id) || this.onboardings().find(o => o.id === id);
    if (match) {
        this.activeOnboarding.set(match);
    }
  }

  getAll(): Observable<Onboarding[]> {
    return this._http.get<OnboardingListResponse>(`${apiUrl}/Onboarding/GetAllOnboarding`).pipe(
      map((res) => {
        if (res.state === 1) {
          this.onboardings.set(res.data);
          return res.data;
        }
        throw new Error(res.message);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  resetUserOnboardings(): void {
    this._hasLoadedUserOnboardings = false;
    this._pendingOnboardingsRequest$ = null;
    this.userOnboardings.set([]);
    this.pendingOnboardings.set([]);
  }

  getPendingOnboardings(forceRefresh = false): Observable<Onboarding[]> {
    if (!forceRefresh && this._hasLoadedUserOnboardings) {
      return of(this.userOnboardings());
    }

    if (!forceRefresh && this._pendingOnboardingsRequest$) {
      return this._pendingOnboardingsRequest$;
    }

    this._pendingOnboardingsRequest$ = this._http.get<OnboardingListResponse>(`${apiUrl}/Onboarding/GetPendingOnboardings`).pipe(
      map((res) => {
        if (res.state === 1) {
          // Endpoint returns all onboardings related to the current user (role-matched),
          // including completed ones. UI can decide what to show using `isCompleted`.
          this._hasLoadedUserOnboardings = true;
          this.userOnboardings.set(res.data);
          this.pendingOnboardings.set(res.data.filter(o => !o.isCompleted && o.isActive));
          return res.data;
        }
        throw new Error(res.message);
      }),
      finalize(() => {
        this._pendingOnboardingsRequest$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
      catchError((err) => throwError(() => err))
    );

    return this._pendingOnboardingsRequest$;
  }

  create(onboarding: any, files: (File | null)[]): Observable<Onboarding> {
    const formData = new FormData();
    
    formData.append('Name', onboarding.name);
    formData.append('Route', onboarding.route);
    formData.append('Position', onboarding.position.toString());
    formData.append('Size', onboarding.size);
    formData.append('IsActive', onboarding.isActive.toString());
    if (onboarding.version) formData.append('Version', onboarding.version);
    if (onboarding.userId) formData.append('UserId', onboarding.userId);
    if (onboarding.helpButtonLocation) formData.append('HelpButtonLocation', onboarding.helpButtonLocation);

    if (onboarding.allowedRoles && onboarding.allowedRoles.length > 0) {
      onboarding.allowedRoles.forEach((role, idx) => {
        formData.append(`AllowedRoles[${idx}]`, role);
      });
    }

    onboarding.items.forEach((item, index) => {
      formData.append(`Items[${index}].Title`, item.title);
      formData.append(`Items[${index}].Description`, item.description || '');
      formData.append(`Items[${index}].MediaType`, item.mediaType);
      formData.append(`Items[${index}].SortPosition`, item.sortPosition.toString());
      formData.append(`Items[${index}].MediaUrl`, item.mediaUrl || '');
      
      if (files[index]) {
        formData.append(`Items[${index}].File`, files[index] as File);
      }
    });

    return this._http.post<OnboardingResponse>(`${apiUrl}/Onboarding/SaveOnboarding`, formData).pipe(
      map((res) => {
        if (res.state === 1) {
          return res.data;
        }
        throw new Error(res.message);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  update(id: number, onboarding: any, files: (File | null)[]): Observable<Onboarding> {
    const formData = new FormData();
    
    formData.append('Name', onboarding.name);
    formData.append('Route', onboarding.route);
    formData.append('Position', onboarding.position.toString());
    formData.append('Size', onboarding.size);
    formData.append('IsActive', onboarding.isActive.toString());
    if (onboarding.version) formData.append('Version', onboarding.version);
    if (onboarding.userId) formData.append('UserId', onboarding.userId);
    if (onboarding.helpButtonLocation) formData.append('HelpButtonLocation', onboarding.helpButtonLocation);

    if (onboarding.allowedRoles && onboarding.allowedRoles.length > 0) {
      onboarding.allowedRoles.forEach((role, idx) => {
        formData.append(`AllowedRoles[${idx}]`, role);
      });
    }

    onboarding.items.forEach((item, index) => {
      formData.append(`Items[${index}].Title`, item.title);
      formData.append(`Items[${index}].Description`, item.description || '');
      formData.append(`Items[${index}].MediaType`, item.mediaType);
      formData.append(`Items[${index}].SortPosition`, item.sortPosition.toString());
      formData.append(`Items[${index}].MediaUrl`, item.mediaUrl || '');
      
      if (files[index]) {
        formData.append(`Items[${index}].File`, files[index] as File);
      }
    });

    return this._http.put<OnboardingResponse>(`${apiUrl}/Onboarding/UpdateOnboarding/${id}`, formData).pipe(
      map((res) => {
        if (res.state === 1) {
          return res.data;
        }
        throw new Error(res.message);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  delete(id: number): Observable<boolean> {
    return this._http.delete<APIResponse<boolean>>(`${apiUrl}/Onboarding/DeleteOnboarding/${id}`).pipe(
      map((res) => {
        if (res.state === 1) {
          // Update local signal if needed
          this.onboardings.update(list => list.filter(o => o.id !== id));
          this.pendingOnboardings.update(list => list.filter(o => o.id !== id));
          this.userOnboardings.update(list => list.filter(o => o.id !== id));
          return true;
        }
        throw new Error(res.message);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  duplicate(id: number): Observable<Onboarding> {
    return this._http.post<OnboardingResponse>(`${apiUrl}/Onboarding/DuplicateOnboarding/${id}`, {}).pipe(
      map((res) => {
        if (res.state === 1) {
          this.onboardings.update(list => [...list, res.data]);
          return res.data;
        }
        throw new Error(res.message);
      }),
      catchError((err) => throwError(() => err))
    );
  }

  markAsCompleted(id: number, isSkipped = false): Observable<boolean> {
    return this._http.post<APIResponse<boolean>>(`${apiUrl}/Onboarding/CompleteOnboarding/${id}`, { isSkipped }).pipe(
      map((res) => {
        if (res.state === 1) {
          // Update local signals
          const completedAt = new Date().toISOString();
          const completedOnboarding = (o: Onboarding): Onboarding =>
            o.id === id ? { ...o, isCompleted: true, completedAt, isSkipped } : o;

          this.pendingOnboardings.update(list => list.filter(o => o.id !== id));
          this.onboardings.update(list => list.map(completedOnboarding));
          this.userOnboardings.update(list => list.map(completedOnboarding));
          return true;
        }
        throw new Error(res.message);
      }),
      catchError((err) => throwError(() => err))
    );
  }
}
